import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './KitActivationFlow.css';



const toCamelCase = (str) => {
  if (!str) return '';
  // Remove special characters, handle spaces and capitalize next letter
  return str.replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : '').replace(/^\w/, c => c.toLowerCase());
};

const ProgressTracker = ({ current, total }) => {
    const progress = total > 1 ? ((current + 1) / total) * 100 : 0;
    return (
        <div className="SACT-progress-tracker">
            <div className="SACT-progress-bar" style={{ width: `${progress}%` }} />
        </div>
    );
};
const setCookie = (name, value, days = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};
const KitActivationFlow = ({ user, onComplete }) => {
  // Overall flow state
  const [step, setStep] = useState('verify'); 
  const [activationId, setActivationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [kitData, setKitData] = useState(null);
  const [userObject, setUserObject] = useState(user);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  
  // State for the new multi-step registration
  const [regStep, setRegStep] = useState(0);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [formData, setFormData] = useState({ identifierType: 'email' });

  // State for identifier check
  const [isCheckingIdentifier, setIsCheckingIdentifier] = useState(false);
  const [identifierError, setIdentifierError] = useState('');

  // Kit Questions state
  const [kitSpecificQuestions, setKitSpecificQuestions] = useState([]);
  const [kitAnswers, setKitAnswers] = useState({});

  // State for consent flow
  const [consentChecked, setConsentChecked] = useState(false);
  const [showConsentText, setShowConsentText] = useState(false);
  const [consentText, setConsentText] = useState('');
  const [isConsentLoading, setIsConsentLoading] = useState(false);

  // --- Step 1: VERIFY KIT ---
  const handleVerifyKit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('https://service9004.szapfs.org/api/kit-activation/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activationId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Verification failed');
      
      setKitData(data);
      // *** THIS IS THE GATEKEEPER FOR THE USER FLOW ***
      if (userObject) {
        setStep('kitQuestions'); // Logged-in users skip registration.
      } else {
        setStep('register'); // New users must register.
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Step 2: FETCH & GROUP QUESTIONS ---
  useEffect(() => {
    const groupQuestions = (questions) => {
        const accountQuestionNames = ["Email Address", "Password", "Confirm Password", "Confirmation Code", "Phone Number"];
        const mainQuestions = questions.filter(q => !accountQuestionNames.includes(q.question));

        const definitions = {
             'Personal Information': ["First Name", "Last Name", "Gender", "Date of Birth"],
             'Physical Health': ["Weight (kg)", "Height (cm)", "Smoking Status", "Cigarettes per day", "Alcohol Consumption", "Chronic Diseases (Optional)", "Significant Medical History (Optional)", "Regularly Used Medications (Optional)"],
             'Lifestyle & Goals': ["Daily Activity Level", "Daily Water Intake", "Average Nightly Sleep", "Health Goals (select multiple)"],
             'Location Details': ["Address (Optional)", "City (Optional)", "State / Province (Optional)", "Postal Code (Optional)", "Country"],
        };

        let grouped = Object.keys(definitions).map(groupName => {
            const groupQuestions = definitions[groupName].map(qName => {
                const question = mainQuestions.find(q => q.question === qName);
                if (question) { question.key = toCamelCase(question.question); }
                return question;
            }).filter(Boolean);
            return { groupName, questions: groupQuestions };
        }).filter(group => group.questions.length > 0);
        
        grouped.push({ groupName: 'Account Type', questions: [] });
        grouped.push({ groupName: 'Your Details', questions: [] });
        grouped.push({ groupName: 'Confirm Your Identity', questions: [{ key: 'confirmationCode' }] }); // <-- NEW STEP
        grouped.push({ groupName: 'Secure Your Account', questions: [{key: 'password'}, {key: 'confirmPassword'}] });

        setQuestionGroups(grouped);

        const initialFormData = { identifierType: 'email' };
        questions.forEach(q => {
            const key = toCamelCase(q.question);
            if(q.question.includes('Health Goals')) {
                initialFormData[key] = [];
            } else {
                initialFormData[key] = '';
            }
        });
        setFormData(initialFormData);
    };

    const fetchRegQuestions = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await fetch('https://service9004.szapfs.org/auth/registration-questions');
        const data = await response.json();
        groupQuestions(data.questions || []);
      } catch (err) {
        setError('Could not load registration form.');
      } finally {
        setIsLoading(false);
      }
    };
    // KitActivationFlow.js


    const fetchKitQuestions = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(`https://service9004.szapfs.org/api/kittypes/${kitData.kitTypeId}/questions`);
            const data = await response.json();
            const questions = data.questions || [];
            setKitSpecificQuestions(questions);
            
            const initialAnswers = {};
            questions.forEach(q => {
                initialAnswers[q.id] = q.type === 'boolean' ? null : '';
            });
            setKitAnswers(initialAnswers);
        } catch (err) {
            setError('Could not load kit-specific questions.');
        } finally {
            setIsLoading(false);
        }
    };

    // This logic ensures the right data is fetched for the right step
    if (step === 'register' && questionGroups.length === 0) {
      fetchRegQuestions();
    }
    if (step === 'kitQuestions' && kitData?.kitTypeId) {
        fetchKitQuestions();
    }
  }, [step, kitData, questionGroups.length]);

  // --- Identifier Check (ONLY ON NEXT BUTTON CLICK) ---
  const checkIdentifierAvailability = async (value, type) => {
    if (!value) {
        setIdentifierError('Please enter your ' + (type === 'email' ? 'email address' : 'phone number') + '.');
        return false;
    }
    if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setIdentifierError('Please enter a valid email address.');
        return false;
    }
     if (type === 'phone' && !/^\+?[0-9\s-]{7,}$/.test(value)) {
        setIdentifierError('Please enter a valid phone number.');
        return false;
    }
    setIsCheckingIdentifier(true);
    setIdentifierError('');
    try {
        const response = await fetch('https://service9004.szapfs.org/auth/check-identifier', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: value, type: type })
        });
        const data = await response.json();
        if (data.exists) {
            setIdentifierError(`This ${type} is already in use. Please try a different one.`);
            return false;
        }
        return true;
    } catch (error) {
        setIdentifierError(`Could not verify ${type}. Please try again.`);
        return false;
    } finally {
        setIsCheckingIdentifier(false);
    }
  };

  const handleFormDataChange = (key, value) => {
    setFormData(prev => {
        const newData = {...prev, [key]: value};
        
        // Clear cigarettes per day if user selects "Never" for smoking
        if (key === 'smokingStatus' && value === 'Never') {
            newData.cigarettesPerDay = '';
        }
        
        return newData;
    });
    
    // Clear any previous identifier error when user types (NO VALIDATION ON KEYSTROKE)
    if (key === 'identifier' && identifierError) {
        setIdentifierError('');
    }
  };

  const handleMultiSelectChange = (key, value) => {
    const currentValues = formData[key] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];
    setFormData(prev => ({...prev, [key]: newValues}));
  };

  // --- Submission Handlers ---
// KitActivationFlow.js

// --- Submission Handlers ---
const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match.");
        return;
    }
    // Add a check for the identifierError state as well
    if(identifierError || codeError) {
        setError("Please resolve the errors before continuing.");
        return;
    }
    setIsLoading(true);
    setError('');

    // The data is already in formData, we just need to send it.
    // Let's create a clean payload.
    const finalPayload = {
      firstName: formData.firstName || null,
      lastName: formData.lastName || null,
      email: formData.identifierType === 'email' ? formData.identifier : null,
      phoneNumber: formData.identifierType === 'phone' ? formData.identifier : null,
      password: formData.password, // This is the most important field that was missing context
      sex: formData.gender || null,
      dateOfBirth: formData.dateOfBirth || null,
      weight: formData.weightKg || null,
      height: formData.heightCm || null,
      smokingStatus: formData.smokingStatus || null,
      cigarettesPerDay: formData.cigarettesPerDay || null,
      alcoholStatus: formData.alcoholConsumption || null,
      chronicDiseases: formData.chronicDiseasesOptional || null,
      medicalHistory: formData.significantMedicalHistoryOptional || null,
      medications: formData.regularlyUsedMedicationsOptional || null,
      activityLevel: formData.dailyActivityLevel || null,
      waterIntake: formData.dailyWaterIntake || null,
      sleepHours: formData.averageNightlySleep || null,
      healthGoals: formData.healthGoalsSelectMultiple || [],
      address: formData.addressOptional || null,
      city: formData.cityOptional || null,
      state: formData.stateProvinceOptional || null,
      postalCode: formData.postalCodeOptional || null,
      country: formData.country || null
    };

    try {
        const response = await fetch('https://service9004.szapfs.org/auth/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(finalPayload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Registration failed.');
        
        // IMPORTANT: We now have the new user data from the API response
        setUserObject(data.user);
        setStep('kitQuestions'); // After registration, proceed to kit questions

    } catch (err) {
        setError(err.message);
    } finally {
        setIsLoading(false);
    }
};

  // KitActivationFlow.js

// ...

const handleCompleteActivation = async (e) => {
    e.preventDefault();
    if (!userObject) {
      setError("Critical error: User data is missing. Please start over.");
      return;
    }
    
    setIsLoading(true);
    setError('');
    try {
      const payload = {
        userId: userObject.User_ID,
        userIdentifier: userObject.identifier,
        activationId: activationId,
        answers: kitAnswers
      };
      
      const activationResponse = await fetch('https://service9004.szapfs.org/api/activate-kit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
      });
      const activationData = await activationResponse.json();
      if (!activationResponse.ok) throw new Error(activationData.message || 'Could not complete activation.');
      
      // =======================================================================
      //  START: THE CRITICAL FIX
      // =======================================================================

      // Check if the 'user' prop passed to this component was initially null.
      // This is our signal that this was a new user registration flow.
      if (!user) {
        console.log('[Activation] New user flow detected. Performing auto-login...');
        
        // Use the credentials from the registration form to sign in.
        const loginResponse = await fetch('https://service9004.szapfs.org/auth/sign-in', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                identifier: formData.identifier, // The email/phone they just entered
                password: formData.password      // The password they just created
            }),
        });

        const loginData = await loginResponse.json();
        if (!loginResponse.ok) {
            // This is unlikely, but handle it just in case.
            throw new Error(loginData.message || 'Auto-login failed after activation.');
        }
        
        // Manually set the session data in the browser, exactly like the main login component does.
        localStorage.setItem('currentUser', JSON.stringify(loginData.user));
        setCookie('ACT-Cookie', loginData.token, 30); // Use the helper function
        
        console.log('[Activation] Auto-login successful. Session created.');
      }
      
      // =======================================================================
      //  END: THE CRITICAL FIX
      // =======================================================================

      // Now, call the original onComplete function.
      // - For existing users, this reloads the dashboard.
      // - For new users, this reloads into a logged-in state because we just set the session data.
      onComplete();

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
};
// Add this new handler function inside the component
const handleVerifyCode = async () => {
    const { identifier, confirmationCode } = formData;
    if (!confirmationCode) {
        setCodeError('Please enter the code you received.');
        return;
    }

    setIsVerifyingCode(true);
    setCodeError('');
    try {
        const response = await fetch('https://service9004.szapfs.org/auth/verify-confirmation-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier, code: confirmationCode })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message);
        
        // Success! Move to the next step.
        setRegStep(s => s + 1);
    } catch (err) {
        setCodeError(err.message || 'An error occurred during verification.');
    } finally {
        setIsVerifyingCode(false);
    }
};

// This function will now handle moving from "Your Details" to "Confirm Identity"
const handleNextAfterIdentifierCheck = async () => {
    // First, check if identifier is available
    const isValid = await checkIdentifierAvailability(formData.identifier, formData.identifierType);
    if (!isValid) return; // Stop if email/phone is taken or invalid

    // If available, send the code
    setIsSendingCode(true);
    setError(''); // Clear general errors
    try {
        const response = await fetch('https://service9004.szapfs.org/auth/send-confirmation-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: formData.identifier, type: formData.identifierType })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message);
        
        // Success! Move to the code entry step.
        setRegStep(s => s + 1);
    } catch (err) {
        // Show the error on the current step if sending fails
        setIdentifierError(err.message || 'Could not send confirmation code.');
    } finally {
        setIsSendingCode(false);
    }
};
// ... the rest of your component

  // --- RENDER FUNCTIONS ---
  
  // THIS FUNCTION IS ONLY FOR NEW USERS
  const renderRegistrationStep = () => {
    if (questionGroups.length === 0) return <div className="SACT-loading-spinner" />;
    const currentGroup = questionGroups[regStep];
    const renderQuestion = (q) => {
        // Don't render cigarettes per day here - it's handled separately
        if (q.key === 'cigarettesPerDay') {
            return null;
        }
        
        if (q.question.includes('select multiple')) {
            return (
                <div key={q.key} className="SACT-question-group SACT-checkbox-group">
                    <label className="SACT-option-label">{q.question.replace(' (select multiple)', '')}</label>
                    <div className="SACT-checkbox-options">
                        {q.options.map(opt => (
                            <label key={opt} className="SACT-checkbox-label">
                                <input type="checkbox" checked={(formData[q.key] || []).includes(opt)} onChange={() => handleMultiSelectChange(q.key, opt)} />
                                {opt}
                            </label>
                        ))}
                    </div>
                </div>
            );
        }
        if (q.options && q.options.length > 0) {
            // For health-related questions, use button-style options instead of dropdowns
            const isHealthSection = currentGroup.groupName === 'Physical Health' || currentGroup.groupName === 'Lifestyle & Goals';
            
            if (isHealthSection) {
                // Special layout for smoking and alcohol status - put them on same row
                const isSmokingOrAlcohol = q.key === 'smokingStatus' || q.key === 'alcoholConsumption';
                const containerClass = isSmokingOrAlcohol ? 'SACT-option-group half-width' : 'SACT-option-group full-width';
                
                return (
                    <div key={q.key} className={containerClass}>
                        <label className="SACT-option-label">{q.question}</label>
                        <div className="SACT-option-buttons">
                            {q.options.map(opt => (
                                <button
                                    key={opt}
                                    type="button"
                                    className={`SACT-option-btn ${formData[q.key] === opt ? 'active' : ''}`}
                                    onClick={() => handleFormDataChange(q.key, opt)}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            }
            
            // Keep dropdowns for other sections
            return (
                 <div key={q.key} className="SACT-input-group select-group">
                    <label htmlFor={q.key} className="select-label">{q.question}</label>
                    <select id={q.key} name={q.key} value={formData[q.key] || ''} onChange={(e) => handleFormDataChange(q.key, e.target.value)}>
                         <option value="" disabled>-- Select --</option>
                        {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
            )
        }
        const inputType = q.question.includes('Date') ? 'date' : q.question.includes('Password') ? 'password' : q.question.includes('Email') ? 'email' : 'text';
        return (
            <div key={q.key} className="SACT-input-group">
                <input type={inputType} id={q.key} name={q.key} value={formData[q.key] || ''} onChange={(e) => handleFormDataChange(q.key, e.target.value)} placeholder=" " required={q.key === 'firstName' || q.key === 'lastName' || q.key === 'country'} />
                <label htmlFor={q.key}>{q.question.replace(' (Optional)', '')}</label>
            </div>
        );
    };
    const renderPhysicalHealthContent = () => {
        const questions = currentGroup.questions;
        const smokingQ = questions.find(q => q.key === 'smokingStatus');
        const alcoholQ = questions.find(q => q.key === 'alcoholConsumption');
        const cigarettesQ = questions.find(q => q.key === 'cigarettesPerDay');
        const chronicDiseasesQ = questions.find(q => q.key === 'chronicDiseasesOptional');
        const medicationsQ = questions.find(q => q.key === 'regularlyUsedMedicationsOptional');
        const medicalHistoryQ = questions.find(q => q.key === 'significantMedicalHistoryOptional');
        const otherMedicalQuestions = questions.filter(q => 
            !['smokingStatus', 'alcoholConsumption', 'cigarettesPerDay', 'chronicDiseasesOptional', 'regularlyUsedMedicationsOptional', 'significantMedicalHistoryOptional'].includes(q.key)
        );

        const renderOptionButtons = (question) => (
            <div key={question.key} className="SACT-option-group half-width">
                <label className="SACT-option-label">{question.question}</label>
                <div className="SACT-option-buttons">
                    {question.options.map(opt => (
                        <button
                            key={opt}
                            type="button"
                            className={`SACT-option-btn ${formData[question.key] === opt ? 'active' : ''}`}
                            onClick={() => handleFormDataChange(question.key, opt)}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            </div>
        );

        const renderTextInput = (question, isFullWidth = false, isTextarea = false) => (
            <div key={question.key} className={`SACT-input-group ${isFullWidth ? 'full-width' : ''} ${isTextarea ? 'textarea-group' : ''}`}>
                {isTextarea ? (
                    <textarea 
                        id={question.key} 
                        name={question.key} 
                        value={formData[question.key] || ''} 
                        onChange={(e) => handleFormDataChange(question.key, e.target.value)} 
                        placeholder=" "
                        rows="4"
                    />
                ) : (
                    <input 
                        type="text" 
                        id={question.key} 
                        name={question.key} 
                        value={formData[question.key] || ''} 
                        onChange={(e) => handleFormDataChange(question.key, e.target.value)} 
                        placeholder=" " 
                    />
                )}
                <label htmlFor={question.key}>{question.question.replace(' (Optional)', '')}</label>
            </div>
        );

        const showCigarettesInput = ['Smoker', 'Socially'].includes(formData.smokingStatus);

        return (
            <>
                {/* Row 1: Smoking and Alcohol */}
                {smokingQ && renderOptionButtons(smokingQ)}
                {alcoholQ && renderOptionButtons(alcoholQ)}
                
                {/* Row 2: Cigarettes per day (conditionally) + empty space */}
                {cigarettesQ && showCigarettesInput && (
                    <div key={cigarettesQ.key} className="SACT-input-group">
                        <input 
                            type="number" 
                            id={cigarettesQ.key} 
                            name={cigarettesQ.key} 
                            value={formData[cigarettesQ.key] || ''} 
                            onChange={(e) => handleFormDataChange(cigarettesQ.key, e.target.value)} 
                            placeholder=" " 
                        />
                        <label htmlFor={cigarettesQ.key}>{cigarettesQ.question}</label>
                    </div>
                )}
                {/* Empty div to maintain grid layout */}
                {showCigarettesInput && <div></div>}
                
                {/* Separator */}
                <div className="SACT-section-separator"></div>
                
                {/* Other Medical Questions (Weight, Height) */}
                {otherMedicalQuestions.map(renderQuestion)}
                
                {/* Chronic Diseases and Medications - Side by Side */}
                {chronicDiseasesQ && renderTextInput(chronicDiseasesQ)}
                {medicationsQ && renderTextInput(medicationsQ)}
                
                {/* Medical History - Full Width and Tall */}
                {medicalHistoryQ && renderTextInput(medicalHistoryQ, true, true)}
            </>
        );
    };

    const renderStepContent = () => {
        switch(currentGroup.groupName) {
            case 'Account Type':
                return ( <div className="SACT-choice-container"> <button type="button" className={`SACT-choice-btn ${formData.identifierType === 'email' ? 'active' : ''}`} onClick={() => setFormData(p => ({...p, identifierType: 'email'}))}> Register with Email </button> <button type="button" className={`SACT-choice-btn ${formData.identifierType === 'phone' ? 'active' : ''}`} onClick={() => setFormData(p => ({...p, identifierType: 'phone'}))}> Register with Phone </button> </div> );
            case 'Physical Health':
                return renderPhysicalHealthContent();
            case 'Your Details':
                 const type = formData.identifierType;
                 return ( 
                    <div className="SACT-identifier-container">
                        {/* ERROR APPEARS ABOVE INPUT */}
                        <div className="SACT-identifier-feedback">
                            {isCheckingIdentifier && <span className="SACT-identifier-status checking">Checking availability...</span>} 
                            {identifierError && <span className="SACT-identifier-status error">{identifierError}</span>}
                        </div>
                        <div className="SACT-input-group"> 
                            <input type={type === 'email' ? 'email' : 'tel'} id="identifier" name="identifier" value={formData.identifier || ''} onChange={(e) => handleFormDataChange('identifier', e.target.value)} placeholder=" " required /> 
                            <label htmlFor="identifier">{type === 'email' ? 'Email Address' : 'Phone Number'}</label> 
                        </div>
                    </div>
                 );
            case 'Confirm Your Identity':
            return (
                <div className="SACT-identifier-container">
                    <p className="SACT-flow-subtitle">We've sent a 6-digit code to {formData.identifier}. Please enter it below.</p>
                    <div className="SACT-identifier-feedback">
                        {isVerifyingCode && <span className="SACT-identifier-status checking">Verifying...</span>}
                        {codeError && <span className="SACT-identifier-status error">{codeError}</span>}
                    </div>
                    <div className="SACT-input-group"> 
                        <input 
                            type="text" 
                            id="confirmationCode" 
                            name="confirmationCode" 
                            value={formData.confirmationCode || ''} 
                            onChange={(e) => handleFormDataChange('confirmationCode', e.target.value)} 
                            placeholder=" " 
                            required 
                            maxLength="6"
                        /> 
                        <label htmlFor="confirmationCode">Confirmation Code</label> 
                    </div>
                </div>
            );
            case 'Secure Your Account':
                return <> <div className="SACT-input-group"> <input type="password" id="password" value={formData.password || ''} onChange={(e) => handleFormDataChange('password', e.target.value)} placeholder=" " required /> <label htmlFor="password">Password</label> </div> <div className="SACT-input-group"> <input type="password" id="confirmPassword" value={formData.confirmPassword || ''} onChange={(e) => handleFormDataChange('confirmPassword', e.target.value)} placeholder=" " required /> <label htmlFor="confirmPassword">Confirm Password</label> </div> </>;
            default:
                 return currentGroup.questions.map(renderQuestion);
        }
    };
    return (
        <form onSubmit={handleRegistrationSubmit} className="SACT-registration-form">
            {/* TOP: Title and Progress Bar */}
            <div className="SACT-form-header">
                <h3 className="SACT-flow-title-q">{currentGroup.groupName}</h3>
                <ProgressTracker current={regStep} total={questionGroups.length} />
                <p className="SACT-flow-subtitle">Step {regStep + 1} of {questionGroups.length}</p>
            </div>

            {/* MIDDLE: Scrollable Questions Area */}
            <div className='SACT-Q-FORM'>
                <div className={`SACT-registration-grid ${currentGroup.questions.length <= 1 ? 'single-item' : ''}`}>
                    {renderStepContent()}
                </div>
                {error && <div className="SACT-error-message">{error}</div>}
            </div>

            {/* BOTTOM: Navigation Buttons */}
            <div className="SACT-form-navigation">
            {regStep > 0 ? ( 
                <button type="button" className="SACT-link-rewind" onClick={() => {
    setCodeError(''); // Clear code-specific error on go-back
    setRegStep(s => s - 1);
}}> ← Back </button>             ) : <div />}
            
            {regStep < questionGroups.length - 1 ? (
                <motion.button 
                    type="button" 
                    className="SACT-btn SACT-btn-login" 
// REPLACE IT WITH THIS:
onClick={() => {
    // START: ADD THIS VALIDATION BLOCK
    if (currentGroup.groupName === 'Personal Information') {
        if (!formData.firstName || !formData.lastName) {
            setError('First Name and Last Name are required.');
            return; // This stops the function from continuing
        }
    }
    setError(''); // Clear error if validation passes
    // END: ADD THIS VALIDATION BLOCK

    if (currentGroup.groupName === 'Your Details') {
        handleNextAfterIdentifierCheck();
    } else if (currentGroup.groupName === 'Confirm Your Identity') {
        handleVerifyCode();
    } else {
        setRegStep(s => s + 1);
    }
}}
                    disabled={isCheckingIdentifier || isSendingCode || isVerifyingCode}
                > 
                    {isCheckingIdentifier ? 'Checking...' : 
                     isSendingCode ? 'Sending Code...' :
                     isVerifyingCode ? 'Verifying...' : 'Next →'} 
                </motion.button>
            ) : (
                <motion.button type="submit" className="SACT-btn SACT-btn-login" disabled={isLoading}> 
                    {isLoading ? 'Creating Account...' : 'Create Account & Continue'} 
                </motion.button>
            )}
        </div>
        </form>
    );
  };
    

const fetchConsentText = async () => {
    if (!kitData?.kitTypeId) {
        setConsentText("This kit type does not have a specific consent form. By activating, you agree to our standard terms of service and privacy policy.");
        setShowConsentText(true);
        return;
    }
    setIsConsentLoading(true);
    setShowConsentText(true);
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/kit/consent-form/${kitData.kitTypeId}`);
        if (!response.ok) {
            const errorData = await response.text();
            throw new Error(errorData || 'Failed to fetch consent information.');
        }
        const data = await response.json();
        setConsentText(data.consentHtml || 'No consent information provided for this kit type.');
    } catch (err) {
        console.error("Consent fetch error:", err);
        setConsentText('Failed to load consent details. Please try again. You can proceed with activation if you agree to our general terms.');
    } finally {
        setIsConsentLoading(false);
    }
};

// In KitActivationFlow.js

const renderKitQuestionsStep = () => {
    if (isLoading && kitSpecificQuestions.length === 0 && step === 'kitQuestions') {
        return <div className="SACT-loading-spinner" />;
    }

    const handleAnswerChange = (id, value, type) => {
        setKitAnswers(prev => ({ ...prev, [id]: type === 'boolean' ? (value === 'true') : value }));
    };

    if (showConsentText) {
        return (
            <div className="SACT-form SACT-consent-view">
                <div className="SACT-form-header">
                    <button type="button" className="SACT-link-button" onClick={() => setShowConsentText(false)}>
                        ← Back to Questions
                    </button>
                    <h3 className="SACT-flow-title">Data Handling & Consent</h3>
                </div>
                <div className="SACT-consent-text-container" dangerouslySetInnerHTML={{ __html: consentText }}>
                    {isConsentLoading && <div className="SACT-loading-spinner" />}
                </div>
            </div>
        );
    }

    return (
      <form onSubmit={handleCompleteActivation} className="SACT-form SACT-kit-questions-form">
          <div className="SACT-form-header-q">
            <button type="button" className="SACT-link-button" onClick={() => setStep('verify')}>
                ← Back
            </button>
            <h3 className="SACT-flow-title-ACT">Activate Your {kitData?.Name || 'Kit'}</h3>
            <p className="SACT-flow-subtitle-ACT">Please answer the questions below.</p>
          </div>
          
          <div className="SACT-kit-questions-container">
            {kitSpecificQuestions.map(q => {
              const { id, question, type, options, inputType, placeholder, disclaimer, isOptional } = q;
              
              return (
                <div key={id} className={`SACT-question-group ${q.layout?.width === 'half' ? 'half-width' : ''}`}>
                  <label className="SACT-question-label">
                    {question}
                    {isOptional && <span className="SACT-optional-tag"> (Optional)</span>}
                  </label>
                  
                  {type === 'boolean' && (
                    <div className="SACT-radio-group">
                      <label><input type="radio" name={id} value="true" checked={kitAnswers[id] === true} onChange={(e) => handleAnswerChange(id, e.target.value, type)} /> Yes</label>
                      <label><input type="radio" name={id} value="false" checked={kitAnswers[id] === false} onChange={(e) => handleAnswerChange(id, e.target.value, type)} /> No</label>
                    </div>
                  )}

                  {type === 'text' && (
                     <div className="SACT-input-group">
                        {/* MODIFIED: Use the 'placeholder' prop directly, and remove the floating label */}
                        <input 
                          type={inputType || 'text'} 
                          id={id} name={id} 
                          value={kitAnswers[id] || ''} 
                          onChange={(e) => handleAnswerChange(id, e.target.value, type)} 
                          placeholder={placeholder || ''} 
                          required={!isOptional}
                        />
                     </div>
                  )}
                  
                  {type === 'textarea' && (
                     <div className="SACT-input-group textarea-group">
                        {/* MODIFIED: Use the 'placeholder' prop directly, and remove the floating label */}
                        <textarea 
                          id={id} 
                          name={id} 
                          value={kitAnswers[id] || ''} 
                          onChange={(e) => handleAnswerChange(id, e.target.value, type)} 
                          placeholder={placeholder || ''} 
                          required={!isOptional} 
                          rows="4"
                        />
                     </div>
                  )}

                   {type === 'select' && Array.isArray(options) && (
                     <div className="SACT-input-group select-group">
                        <select id={id} name={id} value={kitAnswers[id] || ''} onChange={(e) => handleAnswerChange(id, e.target.value, type)} required={!isOptional}>
                           {/* MODIFIED: Use the placeholder in the disabled option for better UX */}
                           <option value="" disabled>{placeholder || '-- Select an option --'}</option>
                           {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        {/* REMOVED: The floating label for the select box is no longer needed */}
                     </div>
                  )}
                   {type === 'health-buttons' && (
                        <div className="SACT-option-buttons">
                            {options.map(opt => (
                                <button key={opt} type="button" className={`SACT-option-btn ${kitAnswers[id] === opt ? 'active' : ''}`} onClick={() => handleAnswerChange(id, opt, type)}>
                                    {opt}
                                </button>
                            ))}
                        </div>
                    )}
                     {type === 'health-checkbox' && (
                        <div className="SACT-checkbox-options">
                            {options.map(opt => (
                                <label key={opt} className="SACT-checkbox-label">
                                    <input type="checkbox" name={id} value={opt} checked={kitAnswers[id]?.includes(opt) || false}
                                        onChange={(e) => {
                                            const newAnswers = kitAnswers[id] ? [...kitAnswers[id]] : [];
                                            if (e.target.checked) {
                                                newAnswers.push(opt);
                                            } else {
                                                const index = newAnswers.indexOf(opt);
                                                if (index > -1) newAnswers.splice(index, 1);
                                            }
                                            handleAnswerChange(id, newAnswers, type);
                                        }}
                                    />
                                    {opt}
                                </label>
                            ))}
                        </div>
                    )}

                  {disclaimer && (
                    <p className="SACT-question-disclaimer">{disclaimer}</p>
                  )}
                </div>
              )
            })}
             {kitSpecificQuestions.length === 0 && !isLoading && (
                <div className="SACT-questions-placeholder">
                    <p>This kit does not require any additional information to activate.</p>
                </div>
            )}
          </div>
          
          <div className="SACT-form-footer-q">
            <div className="SACT-consent-checkbox">
                <input 
                    type="checkbox" 
                    id="consent" 
                    checked={consentChecked} 
                    onChange={(e) => setConsentChecked(e.target.checked)} 
                />
                <label htmlFor="consent">
                    I have read and agree to the <span className="SACT-link-like" onClick={fetchConsentText}>Data Handling & Consent</span>.
                </label>
            </div>
            {error && <div className="SACT-error-message">{error}</div>}
            <motion.button type="submit" className="SACT-btn SACT-btn-login" disabled={isLoading || !consentChecked}>
                {isLoading ? 'Activating...' : 'Activate Kit'}
            </motion.button>
          </div>
      </form>
    );
};
  const renderCurrentStep = () => {
    switch (step) {
      case 'verify': return (
        <form onSubmit={handleVerifyKit} className="SACT-form">
          <h3 className="SACT-flow-title">Activate Your Kit</h3>
          <p className="SACT-flow-subtitle">Please enter the activation ID found in your kit.</p>
          <div className="SACT-input-group">
            <input type="text" id="activationId" value={activationId} onChange={(e) => setActivationId(e.target.value.toUpperCase())} placeholder=" " required />
            <label htmlFor="activationId">Activation ID</label>
          </div>
          {error && <div className="SACT-error-message">{error}</div>}
          <motion.button type="submit" className="SACT-btn SACT-btn-login" disabled={isLoading}>
            {isLoading ? 'Verifying...' : 'Verify Kit'}
          </motion.button>
          <button type="button" className="SACT-link-button" onClick={onComplete}>
              ← Back
          </button>
        </form>
      );
      case 'register': return renderRegistrationStep();
      case 'kitQuestions': return renderKitQuestionsStep();
      default: return <div className="SACT-loading-spinner" />;
    }
  };

  return (
    <div className={`SACT-form-inner SACT-activation-flow ${step === 'register' || step === 'kitQuestions' ? 'registration-active' : ''}`}>
      {renderCurrentStep()}
    </div>
  );
};

export default KitActivationFlow;